import { useCallback, useEffect, useRef, useState } from 'react';
import { calculateDecay, type DecayCurve, type DecayResult } from '@/lib/decay';

export type SpeedMultiplier = 1 | 10 | 60;

export interface DecayTimerControls {
  reset: () => void;
  setSpeed: (speed: SpeedMultiplier) => void;
}

export interface DecayTimerResult extends DecayResult {
  elapsed: number;
  speed: SpeedMultiplier;
  controls: DecayTimerControls;
}

export function useDecayTimer(
  curve: DecayCurve,
  initialSpeed: SpeedMultiplier = 1,
): DecayTimerResult {
  const [elapsed, setElapsed] = useState(0);
  const [speed, setSpeedState] = useState<SpeedMultiplier>(initialSpeed);

  const speedRef = useRef(speed);
  const elapsedRef = useRef(0);
  const lastFrameTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number>(0);

  const totalSeconds = curve.expiresAfterMinutes * 60;

  const tick = useCallback(
    (now: number) => {
      if (lastFrameTimeRef.current === null) {
        lastFrameTimeRef.current = now;
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const deltaMs = now - lastFrameTimeRef.current;
      lastFrameTimeRef.current = now;

      const deltaSeconds = (deltaMs / 1000) * speedRef.current;
      const next = elapsedRef.current + deltaSeconds;

      if (next >= totalSeconds) {
        elapsedRef.current = totalSeconds;
        setElapsed(totalSeconds);
        return;
      }

      elapsedRef.current = next;
      setElapsed(next);
      rafRef.current = requestAnimationFrame(tick);
    },
    [totalSeconds],
  );

  useEffect(() => {
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
    };
  }, [tick]);

  const reset = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    elapsedRef.current = 0;
    lastFrameTimeRef.current = null;
    setElapsed(0);
    rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  const setSpeed = useCallback((s: SpeedMultiplier) => {
    speedRef.current = s;
    setSpeedState(s);
  }, []);

  const result = calculateDecay(curve, elapsed);

  return {
    ...result,
    elapsed,
    speed,
    controls: { reset, setSpeed },
  };
}
